import React, { useMemo } from 'react';
import { EntityTypeEnum, LovDTO } from '@api/ringil-api.v2';
import RingilDropdown, { RingilDropdownItem } from '@components/RingilComponents/RingilDropdown';
import useFolders from '@hooks/use-folders/useFolders.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgFolder from '@icons/folder.svg?react';

interface ComponentProps {
  folders: LovDTO[];
  onChange: (folders: LovDTO[]) => void;
  entityType: EntityTypeEnum;
  /** Options to offer; when omitted the company folder LOV (already limited to accessible folders) is loaded. */
  options?: LovDTO[];
  readonly?: boolean;
  dataTestIdBase?: string;
  placeholder?: string;
}

/**
 * RD-2977: multi-select of company folders. The LOV only returns folders the user may access, so a
 * restricted user cannot file an expedition into a folder it does not see.
 */
const InputFolders: React.FC<ComponentProps> = ({ folders, onChange, entityType, options, readonly = false, dataTestIdBase, placeholder }) => {
  const { tr } = useTranslationLgs();
  const loaded = useFolders(entityType);
  const available = options ?? loaded;

  // Keep the currently selected folders selectable even when the LOV no longer lists them (e.g. renamed, or
  // filed by somebody else) so a save does not silently drop them.
  const allOptions = useMemo(() => {
    const byId = new Map<string, LovDTO>();
    available.forEach(f => byId.set(f.id, f));
    (folders ?? []).forEach(f => {
      if (!byId.has(f.id)) byId.set(f.id, f);
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [available, folders]);

  const items: RingilDropdownItem[] = useMemo(
    () => allOptions.map(f => ({ id: f.id, label: f.name, icon: <SvgFolder className="w-4 h-4" />, disabled: f.active === false })),
    [allOptions],
  );

  return (
    <RingilDropdown
      items={items}
      selectedIds={(folders ?? []).map(f => f.id)}
      onSelectionChange={ids => onChange(allOptions.filter(f => ids.includes(f.id)))}
      readonly={readonly}
      multiSelect={true}
      selectAllEnabled={false}
      dataTestIdBase={dataTestIdBase}
      placeholder={placeholder ?? tr('InputFolders.placeholder', 'Vyberte složky...')}
    />
  );
};

export default InputFolders;
